import { useEffect, useMemo, useRef } from 'react';
import type { Contact } from '@/modules/contact';
import { AWARENESS_SCROLL_IDLE_MS } from '@/modules/config/constants';

/**
 * The contacts whose rows currently intersect `container`'s visible box, in DOM
 * order. Rows mark themselves with `data-contact-id` (SearchableContactList), so
 * this reads the live list without knowing how it is built.
 */
export function visibleContactIds(container: HTMLElement): string[] {
  const box = container.getBoundingClientRect();
  const ids: string[] = [];
  container.querySelectorAll<HTMLElement>('[data-contact-id]').forEach(row => {
    const id = row.dataset.contactId;
    if (!id) return;
    const rect = row.getBoundingClientRect();
    if (rect.bottom > box.top && rect.top < box.bottom) ids.push(id);
  });
  return ids;
}

/**
 * Report the screenful of contacts on show, once scrolling settles (#581).
 *
 * Some per-contact facts are too expensive to compute for a whole address book —
 * topic awareness costs OPRF hashing plus a round trip per contact — but cheap
 * enough for the dozen or so rows a person is actually looking at. This watches
 * `container` and hands `onScreenful` those rows after `idleMs` of quiet, so a
 * fast flick down the list resolves where it lands rather than everywhere it
 * passed. `onScreenful` is called with every visible contact, including ones it
 * has already seen; de-duplicating is the caller's job.
 */
export function useScreenfulAwareness({
  container,
  contacts,
  onScreenful,
  idleMs = AWARENESS_SCROLL_IDLE_MS,
}: {
  container: HTMLElement | null;
  contacts: readonly Contact[];
  onScreenful: (contacts: readonly Contact[]) => void;
  idleMs?: number;
}): void {
  const contactsById = useMemo(
    () => new Map(contacts.map(contact => [contact.localId, contact])),
    [contacts]
  );
  // Held in a ref so a caller passing an inline closure doesn't tear down and
  // re-arm the listener on every render.
  const onScreenfulRef = useRef(onScreenful);
  onScreenfulRef.current = onScreenful;

  useEffect(() => {
    if (!container) return;
    let handle: number | undefined;
    const resolve = () => {
      handle = undefined;
      const visible = visibleContactIds(container)
        .map(id => contactsById.get(id))
        .filter((contact): contact is Contact => contact !== undefined);
      if (visible.length > 0) onScreenfulRef.current(visible);
    };
    const schedule = () => {
      if (handle !== undefined) window.clearTimeout(handle);
      handle = window.setTimeout(resolve, idleMs);
    };
    // The first screenful is on show without anyone scrolling, so resolve it on
    // arrival too — waiting for a scroll would leave a short list unresolved.
    schedule();
    container.addEventListener('scroll', schedule, { passive: true });
    return () => {
      if (handle !== undefined) window.clearTimeout(handle);
      container.removeEventListener('scroll', schedule);
    };
  }, [container, contactsById, idleMs]);
}
